// Booking utilization — who holds calendar time and does not fly it.
//
// Flights carry no reference to the booking they belong to (FlightDoc has no
// bookingId), so "this booking was used" has to be inferred: a flight by the
// same lessee on any local date the booking covers. That inference is the whole
// basis of this module, and it is why every count here is a signal to look at a
// renter, never a number to bill against.
//
// Two fairness rules are built in, because without them the report blames the
// wrong people:
//   1. A booking AJM cancelled (cancelReason 'asset_protection' — weather,
//      maintenance, a squawk) is not the renter's doing and is excluded.
//   2. A booking the renter cancelled with real notice gave the slot back,
//      which is the behavior we want. It is counted as released, not held.

import type { BookingDoc, FlightDoc, UserDoc } from './types';

/** Notice, in hours, that separates giving the slot back from effectively not showing. */
export const LATE_CANCEL_HOURS = 24;

export type Outcome =
  | 'flew'          // a flight was logged inside the booked window
  | 'no_show'       // window passed, never cancelled, nothing flown
  | 'late_cancel'   // cancelled inside LATE_CANCEL_HOURS — too late to rebook
  | 'released'      // cancelled with notice; slot went back on the calendar
  | 'cancelled_unknown' // cancelled before cancelledAt was recorded; timing unknowable
  | 'ajm_cancelled' // AJM stood it down; not the renter's doing
  | 'open';         // hasn't finished yet, no verdict possible

export interface Resolved {
  booking: BookingDoc;
  outcome: Outcome;
  heldHours: number;    // duration of the reservation
  flownHours: number;   // hours actually logged against it
  flights: FlightDoc[];
  start: Date;
  end: Date;
}

/** Local YYYY-MM-DD. Deliberately not toISOString(), which shifts the day near midnight. */
export function dateKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Every local date a booking touches, so overnight trips match a flight on any of their days. */
export function datesCovered(start: Date, end: Date): string[] {
  const out: string[] = [];
  const cur = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const last = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  while (cur <= last && out.length < 32) {
    out.push(dateKey(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

const hours = (a: Date, b: Date) => Math.max(0, (b.getTime() - a.getTime()) / 3600000);

/**
 * Pair bookings with the flights that plausibly belong to them.
 *
 * A flight is claimed by at most one booking. Without that, a renter with two
 * bookings on one day and one flight would look like they used both — the exact
 * misuse this page exists to surface would read as clean.
 */
export function resolveBookings(
  bookings: BookingDoc[],
  flights: FlightDoc[],
  now: Date = new Date(),
): Resolved[] {
  const claimed = new Set<string>();

  // Earliest first, so when two bookings on one day compete for a flight the
  // earlier reservation claims it.
  const ordered = [...bookings].sort(
    (a, b) => a.startDateTime.toDate().getTime() - b.startDateTime.toDate().getTime(),
  );

  return ordered.map((booking) => {
    const start = booking.startDateTime.toDate();
    const end = booking.endDateTime.toDate();
    const days = new Set(datesCovered(start, end));

    const matched = flights.filter(
      (f) => f.lesseeUid === booking.lesseeUid && days.has(f.date) && !claimed.has(f.flightId),
    );
    matched.forEach((f) => claimed.add(f.flightId));

    const flownHours = matched.reduce((s, f) => s + (f.hoursFlown || 0), 0);
    const heldHours = hours(start, end);

    let outcome: Outcome;
    if (booking.status === 'cancelled' || booking.status === 'denied') {
      if (booking.cancelReason === 'asset_protection' || booking.status === 'denied') {
        outcome = 'ajm_cancelled';
      } else if (!booking.cancelledAt) {
        // Cancelled before the timestamp was captured. Guessing from any other
        // field would be inventing a fact about someone's behavior.
        outcome = 'cancelled_unknown';
      } else {
        const notice = hours(booking.cancelledAt.toDate(), start);
        outcome = notice >= LATE_CANCEL_HOURS ? 'released' : 'late_cancel';
      }
    } else if (end > now) {
      outcome = 'open';
    } else {
      outcome = matched.length > 0 ? 'flew' : 'no_show';
    }

    return { booking, outcome, heldHours, flownHours, flights: matched, start, end };
  });
}

export interface RenterRow {
  uid: string;
  name: string;
  email: string;
  resolved: Resolved[];
  /** Finished bookings the renter is answerable for (excludes open and AJM-cancelled). */
  accountable: number;
  flew: number;
  noShows: number;
  lateCancels: number;
  released: number;
  /** Cancelled before cancellation times were recorded — timing unknowable. */
  cancelledUnknown: number;
  /** Hours kept off the calendar: everything except released and AJM-cancelled. */
  heldHours: number;
  flownHours: number;
  /** flownHours / heldHours. null when nothing was held. */
  utilization: number | null;
  /** noShows + lateCancels over accountable. null when nothing is accountable. */
  wasteRate: number | null;
  lastNoShow: Date | null;
  /** Start dates of the bookings that went unused, newest first. */
  noShowDates: Date[];
  lateCancelDates: Date[];
  releasedDates: Date[];
}

export function byRenter(resolved: Resolved[], users: UserDoc[]): RenterRow[] {
  const userByUid = new Map(users.map((u) => [u.uid, u]));
  const groups = new Map<string, Resolved[]>();
  for (const r of resolved) {
    const uid = r.booking.lesseeUid;
    const list = groups.get(uid);
    if (list) list.push(r);
    else groups.set(uid, [r]);
  }

  const rows: RenterRow[] = [];
  for (const [uid, rs] of groups) {
    const u = userByUid.get(uid);
    const name =
      `${u?.personal?.firstName ?? ''} ${u?.personal?.lastName ?? ''}`.trim() ||
      rs.find((r) => r.booking.lesseeName)?.booking.lesseeName ||
      'Unknown renter';

    const count = (o: Outcome) => rs.filter((r) => r.outcome === o).length;
    const noShows = count('no_show');
    const lateCancels = count('late_cancel');
    const released = count('released');
    const cancelledUnknown = count('cancelled_unknown');
    const flew = count('flew');
    const accountable = flew + noShows + lateCancels + released + cancelledUnknown;

    // Released time went back on the calendar, so it is not "held".
    const counted = rs.filter((r) => r.outcome === 'flew' || r.outcome === 'no_show' || r.outcome === 'late_cancel');
    const heldHours = counted.reduce((s, r) => s + r.heldHours, 0);
    const flownHours = counted.reduce((s, r) => s + r.flownHours, 0);

    const datesFor = (o: Outcome) =>
      rs.filter((r) => r.outcome === o)
        .map((r) => r.start)
        .sort((a, b) => b.getTime() - a.getTime());
    const noShowDates = datesFor('no_show');
    const lateCancelDates = datesFor('late_cancel');
    const releasedDates = datesFor('released');

    rows.push({
      uid,
      name,
      email: u?.email ?? '',
      resolved: rs.sort((a, b) => b.start.getTime() - a.start.getTime()),
      accountable,
      flew,
      noShows,
      lateCancels,
      released,
      cancelledUnknown,
      heldHours,
      flownHours,
      utilization: heldHours > 0 ? flownHours / heldHours : null,
      wasteRate: accountable > 0 ? (noShows + lateCancels) / accountable : null,
      lastNoShow: noShowDates[0] ?? null,
      noShowDates,
      lateCancelDates,
      releasedDates,
    });
  }

  // Worst first — most wasted bookings, then most hours held without flying.
  return rows.sort(
    (a, b) =>
      b.noShows + b.lateCancels - (a.noShows + a.lateCancels) ||
      b.heldHours - b.flownHours - (a.heldHours - a.flownHours),
  );
}

export const OUTCOME_LABEL: Record<Outcome, string> = {
  flew: 'Flew',
  no_show: 'No-show',
  late_cancel: `Late cancel`,
  released: 'Released in time',
  cancelled_unknown: 'Cancelled',
  ajm_cancelled: 'AJM stood down',
  open: 'Upcoming',
};

export const OUTCOME_BADGE: Record<Outcome, string> = {
  flew: 'badge-success',
  no_show: 'badge-danger',
  late_cancel: 'badge-warning',
  released: 'badge-neutral',
  cancelled_unknown: 'badge-neutral',
  ajm_cancelled: 'badge-neutral',
  open: 'badge-info',
};
